import {
  Approval,
  CalendarToday,
  Category,
  CollectionsBookmark,
  Construction,
  DataThresholding,
  Loyalty,
  ManageSearch,
  Map,
  Public,
  ScreenRotation,
  Style,
  Translate,
  Tune,
  Search,
} from "@mui/icons-material";
import { ButtonV1 } from "../atoms/button";
import { FilterTab, FilterTabV2 } from "./filterTab";
import { getAuthFromLocal, removeAuthFromLocal } from "../../utils/storage";
import { useNavigate } from "react-router-dom";
import { useGetAdTypeQuery } from "../../api/filters/adType";
import { useGetBrandQuery } from "../../api/filters/brand";
import { useGetCategoryQuery } from "../../api/filters/category";
import { useGetFeatureQuery } from "../../api/filters/feature";
import { useGetCountryQuery } from "../../api/filters/country";
import { useGetDtLevelQuery } from "../../api/filters/dtLevels";
import { useGetLanguageQuery } from "../../api/filters/language";
import { useGetOrientationQuery } from "../../api/filters/orientation";
import { useGetRegionQuery } from "../../api/filters/region";
import { useGetStageQuery } from "../../api/filters/stage";
import { useGetSubBrandQuery } from "../../api/filters/subBrand";
import { authDetails } from "../../api/auth/authActions";
import { useDispatch, useSelector } from "react-redux";
import {
  logoutFilterHandler,
  storeHandler,
} from "../../api/store/storeActions";
import { useGetBrandCollabQuery } from "../../api/filters/brandCollab";
import { useGetYearQuery } from "../../api/filters/year";
import { useGetKeywordQuery } from "../../api/filters/keyword";
import { useEffect, useState, useMemo, useRef } from "react";
import { Debouncer } from "../../utils/debaouncer";
import { useGetCreativeUserQuery } from "../../api/creative";
import { CreativeType } from "../../types/types";

interface ItemSchema {
  _id: string;
  title: string;
}

interface props {
  regex: string;
  pageNo: number;
  setPageNo: Function;
  setRegex: Function;
  setKeyword: Function;
  setIsLoading: Function;
  showMenu: boolean;
  setShowMenu: Function;
}

export function SidebarUser({
  regex,
  pageNo,
  setPageNo,
  setRegex,
  setKeyword,
  setIsLoading,
  showMenu,
  setShowMenu,
}: props) {
  const navigate = useNavigate();
  let data: authDetails = getAuthFromLocal();
  !data && navigate("/");
  const [shouldFetch, setShouldFetch] = useState(true);
  const [debouncedBrandFilters, setDebouncedBrandFilters] = useState<any>(null);

  const dispatch = useDispatch<any>();
  const adType = useSelector((state: any) => state.data.adType);
  const brand = useSelector((state: any) => state.data.brand);
  const category = useSelector((state: any) => state.data.category);
  const country = useSelector((state: any) => state.data.country);
  const dtLevel = useSelector((state: any) => state.data.dtLevel);
  const feature = useSelector((state: any) => state.data.feature);
  const language = useSelector((state: any) => state.data.language);
  const orientation = useSelector((state: any) => state.data.orientation);
  const region = useSelector((state: any) => state.data.region);
  const stage = useSelector((state: any) => state.data.stage);
  const subBrand = useSelector((state: any) => state.data.subBrand);
  const brandCollab = useSelector((state: any) => state.data.brandCollab);
  const year = useSelector((state: any) => state.data.year);
  const keyword = useSelector((state: any) => state.data.keyword);
  const selectedCreativeIds = useSelector((state: any) => state.data.selectedCreativeIds) || [];
  const creativeIdsParam = selectedCreativeIds.length > 0 ? selectedCreativeIds.join(",") : undefined;
  const mediaType = useSelector((state: any) => state.data.mediaType) || [];
  const mediaTypeParam = mediaType.length > 0 ? mediaType.join(",") : undefined;

  // Get all creatives to extract filter values from results
  const { data: allCreativesData } = useGetCreativeUserQuery({
    title: regex,
    pageSize: 1000, // Get a large number to capture all creatives for filtering
    pageNo: 1,
    sortType: "title",
    sortOrder: 1,
    adType: adType?.map((item: ItemSchema) => item.title) || undefined,
    brand: brand?.map((item: ItemSchema) => item.title) || undefined,
    category: category?.map((item: ItemSchema) => item.title) || undefined,
    country: country?.map((item: ItemSchema) => item.title) || undefined,
    dt: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    feature: feature?.map((item: ItemSchema) => item.title) || undefined,
    language: language?.map((item: ItemSchema) => item.title) || undefined,
    orientation: orientation?.map((item: ItemSchema) => item.title) || undefined,
    region: region?.map((item: ItemSchema) => item.title) || undefined,
    stage: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrand: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch,
  });

  // Extract unique values from creatives for each filter type
  const creativeFilterValues = useMemo(() => {
    const creatives: CreativeType[] = allCreativesData?.data?.data || [];
    if (creatives.length === 0) return null;

    return {
      adTypes: Array.from(new Set(creatives.flatMap(c => c.adTypes || []))),
      brands: Array.from(new Set(creatives.map(c => c.brandId).filter(Boolean))),
      categories: Array.from(new Set(creatives.flatMap(c => c.categories || []))),
      countries: Array.from(new Set(creatives.flatMap(c => c.countries || []))),
      dtLevels: Array.from(new Set(creatives.map(c => c.dt).filter(Boolean))),
      features: Array.from(new Set(creatives.flatMap(c => c.features || []))),
      languages: Array.from(new Set(creatives.map(c => c.lang).filter(Boolean))),
      orientations: Array.from(new Set(creatives.map(c => c.orientation).filter(Boolean))),
      regions: Array.from(new Set(creatives.map(c => c.regionId).filter(Boolean))),
      stages: Array.from(new Set(creatives.map(c => c.stage).filter(Boolean))),
      subBrands: Array.from(new Set(creatives.flatMap(c => c.subBrands || []))),
      brandCollabs: Array.from(new Set(creatives.flatMap(c => c.brandCollab || []))),
      years: Array.from(new Set(creatives.map(c => c.year).filter(Boolean))),
      keywords: Array.from(new Set(creatives.flatMap(c => c.keywords || []))),
    };
  }, [allCreativesData]);

  // Helper function to filter filter data based on creative values
  // compareById: true for filters where creative data contains IDs (brand, region)
  // compareById: false for filters where creative data contains titles (adType, category, etc.)
  const filterDataByCreatives = (filterData: any[] | undefined, creativeValues: string[] | undefined, compareById: boolean = false) => {
    if (!filterData || !creativeValues || creativeValues.length === 0) {
      return filterData;
    }
    return filterData.filter((item: ItemSchema) =>
      compareById ? creativeValues.includes(item._id) : creativeValues.includes(item.title)
    );
  };


  // Debounce brand filter parameters to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBrandFilters({
        adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
        brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
        categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
        countryId: country?.map((item: ItemSchema) => item.title) || undefined,
        dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
        featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
        languageId: language?.map((item: ItemSchema) => item.title) || undefined,
        orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
        regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
        stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
        year: year?.map((item: ItemSchema) => item.title) || undefined,
        keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
      });
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [adType, brandCollab, category, country, dtLevel, feature, language, orientation, region, stage, year, keyword]);



  const { data: AdTypeData } = useGetAdTypeQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: BrandData } = useGetBrandQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    ...(debouncedBrandFilters || {}), // Use debounced filters
    sortType: "title",
    sortOrder: 1,
  }, {
    skip: !shouldFetch,
  });

  const { data: CategoryData } = useGetCategoryQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: DtLevelData } = useGetDtLevelQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: FeatureData } = useGetFeatureQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    filterType: "active",
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: LanguageData } = useGetLanguageQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: OrientationData } = useGetOrientationQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: RegionData } = useGetRegionQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: StageData } = useGetStageQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: CountryData } = useGetCountryQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: SubBrandData } = useGetSubBrandQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: BrandCollabData } = useGetBrandCollabQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: YearData } = useGetYearQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    keywordsId: keyword?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: -1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  const { data: KeywordData } = useGetKeywordQuery({
    // searchTitle: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    // searchKeyword: regex, // COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
    isPublished: true,
    active: true,
    adTypesId: adType?.map((item: ItemSchema) => item.title) || undefined,
    year: year?.map((item: ItemSchema) => item.title) || undefined,
    brandId: brand?.map((item: ItemSchema) => item.title) || undefined,
    categoryId: category?.map((item: ItemSchema) => item.title) || undefined,
    countryId: country?.map((item: ItemSchema) => item.title) || undefined,
    dtLevelId: dtLevel?.map((item: ItemSchema) => item.title) || undefined,
    featuresId: feature?.map((item: ItemSchema) => item.title) || undefined,
    languageId: language?.map((item: ItemSchema) => item.title) || undefined,
    orientationId: orientation?.map((item: ItemSchema) => item.title) || undefined,
    regionsId: region?.map((item: ItemSchema) => item.title) || undefined,
    stagesId: stage?.map((item: ItemSchema) => item.title) || undefined,
    subBrandId: subBrand?.map((item: ItemSchema) => item.title) || undefined,
    brandCollab: brandCollab?.map((item: ItemSchema) => item.title) || undefined,
    creativeIds: creativeIdsParam,
    sortType: "title",
    sortOrder: 1,
    mediaType: mediaTypeParam,
  }, {
    skip: !shouldFetch, // Skip the query if shouldFetch is false
  });

  function adTypeHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Adtype_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (adType?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        adType: adType.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("adTypeRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        adType: [...adType, newItem],
      };
      //console.log("adTypeAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function brandHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Brand_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (brand?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        brand: brand?.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("brandRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        brand: [...brand, newItem],
      };
      //console.log("brandAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function brandCollabHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_BrandCollab_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (brandCollab?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        brandCollab: brandCollab.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("brandCollabRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        brandCollab: [...brandCollab, newItem],
      };
      //console.log("brandCollabAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function categoryHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Category_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (category?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        category: category.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("categoryRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        category: [...category, newItem],
      };
      //console.log("categoryAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function countryHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Country_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (country?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        country: country.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("countryRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        country: [...country, newItem],
      };
      //console.log("countryAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function dtLevelHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_DtLevel_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (dtLevel?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        dtLevel: dtLevel.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("dtLevelRemoved", data);
      dispatch(storeHandler(data));
      // console.log("🚀 ~ dtLevelHandleChange ~ data:", data)
    } else {
      const data = {
        dtLevel: [...dtLevel, newItem],
      };
      //console.log("dtLevelAdded", data);
      dispatch(storeHandler(data));
      console.log("🚀 ~ dtLevelHandleChange ~ data:", data)
    }
  }

  function featureHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    // Create unique slug by combining _id and title
    const newItemSlug = `${newItem._id}_${newItem.title}`;
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Feature_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);

    // Check using slug for uniqueness
    const featureExists = feature?.some((item: ItemSchema) => {
      const itemSlug = `${item._id}_${item.title}`;
      return itemSlug === newItemSlug;
    });

    if (featureExists) {
      const data = {
        feature: feature.filter((item: ItemSchema) => {
          const itemSlug = `${item._id}_${item.title}`;
          return itemSlug !== newItemSlug;
        }),
      };
      dispatch(storeHandler(data));
    } else {
      const data = {
        feature: [...feature, newItem],
      };
      dispatch(storeHandler(data));
    }
  }

  function languageHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Language_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (language?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        language: language.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("languageRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        language: [...language, newItem],
      };
      //console.log("languageAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function orientationHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Orientation_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (orientation?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        orientation: orientation.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("orientationRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        orientation: [...orientation, newItem],
      };
      //console.log("orientationAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function regionHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Region_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (region?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        region: region.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("regionRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        region: [...region, newItem],
      };
      //console.log("regionAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function stageHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_State_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (stage?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        stage: stage.filter((item: ItemSchema) => item.title !== newItem.title),
      };
      //console.log("stageRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        stage: [...stage, newItem],
      };
      //console.log("stageAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function subBrandHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_SubBrand_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (subBrand?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        subBrand: subBrand.filter(
          (item: ItemSchema) => item.title !== newItem.title
        ),
      };
      //console.log("subBrandRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        subBrand: [...subBrand, newItem],
      };
      //console.log("subBrandAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function yearHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Year_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (year?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        year: year.filter((item: ItemSchema) => item.title !== newItem.title),
      };
      //console.log("yearRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        year: [...year, newItem],
      };
      //console.log("yearAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function keywordHandleChange(newItem: ItemSchema) {
    setShouldFetch(false)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: `Engage_Tap_Keyword_${newItem.title}`,
        category: "User Interaction",
        action: "Button Click",
        label: "Filter Button",
      });
    }
    setPageNo(1);
    setIsLoading(true);
    if (keyword?.some((item: ItemSchema) => item.title === newItem.title)) {
      const data = {
        keyword: keyword.filter((item: ItemSchema) => item.title !== newItem.title),
      };
      //console.log("keywordRemoved", data);
      dispatch(storeHandler(data));
    } else {
      const data = {
        keyword: [...keyword, newItem],
      };
      //console.log("keywordAdded", data);
      dispatch(storeHandler(data));
    }
  }

  function cleanHandler() {
    window.location.reload();
    setRegex("");
    setKeyword("");
    dispatch(logoutFilterHandler());
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "Engage_Tap_Clear",
        category: "User Interaction",
        action: "Button Click",
        label: "Clear Button",
      });
    }
    //console.log("clenind");
  }

  function logoutHandler() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "Engage_Tap_Logout",
        category: "User Interaction",
        action: "Button Click",
        label: "Logout Button",
      });
    }
    removeAuthFromLocal();
    dispatch(logoutFilterHandler());
    navigate("/");
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setShouldFetch(true); // Set shouldFetch to true after debounce delay
    }, 500); // 500ms debounce delay for faster filter updates

    return () => {
      clearTimeout(handler); // Clear timeout if the user types again
    };
    // regex COMMENTED OUT FOR TESTING - to check if sidebar filter updates are causing slowness
  }, [pageNo, /* regex, */ adType, brand, category, dtLevel, country, feature, language, orientation, region, stage, subBrand, brandCollab, year, keyword, mediaType, selectedCreativeIds]);

  return (
    <>
      {/* Desktop Sidebar - unchanged */}
      <div className="sticky top-[10vh] w-[20%] h-[90vh] bg-slate-50 px-[2vw] py-[4vh] flex-col shadow-lg justify-between hidden lg:flex">
        <div className="h-[90%] relative overflow-hidden">
          <div className="flex justify-between w-full h-max pb-6 sticky top-0 bg-slate-50 z-10">
            <p className="flex items-center gap-1 font-semibold text-2xl">
              <Tune className="text-black" style={{ fontSize: "1.8rem" }} />
              Filters
            </p>
            <div>
              <ButtonV1 color="red" setActive={() => cleanHandler()}>
                clear
              </ButtonV1>
            </div>
          </div>
          <div className="h-[90%] pr-2 overflow-y-auto overflow-x-hidden scrollerRed">
            <FilterTab
              child="Region"
              icon={<Public style={{ fontSize: "20px", marginLeft: "10px" }} />}
              handleChange={regionHandleChange}
              reduxData={region}
              data={RegionData?.data?.data}
            />

            {CountryData?.data?.data.length !== 0 && region.length !== 0 && (
              <FilterTab
                child="Country"
                icon={<Map style={{ fontSize: "20px", marginLeft: "10px" }} />}
                handleChange={countryHandleChange}
                reduxData={country}
                data={CountryData?.data?.data}
              />
            )}

            {DtLevelData?.data?.data.length !== 0 && (
              <FilterTab
                child="Dt Levels"
                icon={<DataThresholding style={{ fontSize: "20px", marginLeft: "10px" }} />}
                handleChange={dtLevelHandleChange}
                reduxData={dtLevel}
                data={DtLevelData?.data?.data}
              />
            )}

            <FilterTab
              child="AdType"
              icon={
                <ManageSearch style={{ fontSize: "20px", marginLeft: "10px" }} />
              }
              handleChange={adTypeHandleChange}
              reduxData={adType}
              data={AdTypeData?.data?.data}
            />

            <FilterTab
              child="Category"
              icon={<Category style={{ fontSize: "20px", marginLeft: "10px" }} />}
              handleChange={categoryHandleChange}
              reduxData={category}
              data={CategoryData?.data?.data}
            />

            <FilterTab
              child="Feature"
              icon={
                <Construction style={{ fontSize: "20px", marginLeft: "10px" }} />
              }
              handleChange={featureHandleChange}
              reduxData={feature}
              data={FeatureData?.data?.data}
            />

            <FilterTab
              child="Brand"
              icon={<Style style={{ fontSize: "20px", marginLeft: "10px" }} />}
              handleChange={brandHandleChange}
              reduxData={brand}
              data={BrandData?.data?.data}
            />

            {SubBrandData?.data?.data.length !== 0 && brand.length !== 0 && (
              <FilterTab
                child="SubBrand"
                icon={
                  <Loyalty style={{ fontSize: "20px", marginLeft: "10px" }} />
                }
                handleChange={subBrandHandleChange}
                reduxData={subBrand}
                data={SubBrandData?.data?.data}
              />
            )}

            {BrandCollabData?.data?.data.length !== 0 && brand.length !== 0 && (
              <FilterTab
                child="Brand Collab"
                icon={
                  <CollectionsBookmark
                    style={{ fontSize: "20px", marginLeft: "10px" }}
                  />
                }
                handleChange={brandCollabHandleChange}
                reduxData={brandCollab}
                data={BrandCollabData?.data?.data}
              />
            )}

            <FilterTab
              child="Language"
              icon={
                <Translate style={{ fontSize: "20px", marginLeft: "10px" }} />
              }
              handleChange={languageHandleChange}
              reduxData={language}
              data={LanguageData?.data?.data}
            />

            <FilterTab
              child="Orientation"
              icon={
                <ScreenRotation
                  style={{ fontSize: "20px", marginLeft: "10px" }}
                />
              }
              handleChange={orientationHandleChange}
              reduxData={orientation}
              data={OrientationData?.data?.data}
            />

            <FilterTab
              child="Stage"
              icon={<Approval style={{ fontSize: "20px", marginLeft: "10px" }} />}
              handleChange={stageHandleChange}
              reduxData={stage}
              data={StageData?.data?.data}
            />

            <FilterTab
              child="Year"
              icon={
                <CalendarToday style={{ fontSize: "20px", marginLeft: "10px" }} />
              }
              handleChange={yearHandleChange}
              reduxData={year}
              data={YearData?.data?.data}
            />
          </div>
        </div>
        <button
          type={"button"}
          onClick={() => logoutHandler()}
          className={`w-1/2 mx-auto my-1 flex gap-1 justify-center items-center capitalize p-2 px-4 border-none rounded-xl whitespace-nowrapl hover:bg-[#a90027] text-[#DA1A42] hover:text-white`}
        >
          log out
        </button>
      </div>

      {/* Mobile Filter Modal - matches the design image */}
      {showMenu && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMenu(false)}
          />

          {/* Modal */}
          <div className="absolute right-0 top-0 h-auto mt-4 w-[100%] bg-white shadow-xl">
            {/* Header */}
            <button
              onClick={() => setShowMenu(false)}
              className="text-gray-500 focus:outline-none hover:text-gray-700 text-xl font-bold absolute top-1 right-4"
            >
              ×
            </button>
            <div className="px-[30px] py-[10px]">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 mt-4">
                <div className="flex items-center gap-2">
                  <Tune className="text-black" style={{ fontSize: "1.2rem" }} />
                  <h2 className="text-lg font-semibold text-black">Filters</h2>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => cleanHandler()}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-red-700"
                  >
                    Clear
                  </button>

                </div>
              </div>

              {/* Filter Categories */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                <FilterTab
                  child="Region"
                  icon={<Public style={{ fontSize: "20px", marginLeft: "10px" }} />}
                  handleChange={regionHandleChange}
                  reduxData={region}
                  data={RegionData?.data?.data}
                />

                {CountryData?.data?.data.length !== 0 && region.length !== 0 && (
                  <FilterTab
                    child="Country"
                    icon={<Map style={{ fontSize: "20px", marginLeft: "10px" }} />}
                    handleChange={countryHandleChange}
                    reduxData={country}
                    data={CountryData?.data?.data}
                  />
                )}

                {DtLevelData?.data?.data.length !== 0 && (
                  <FilterTab
                    child="Dt Levels"
                    icon={<DataThresholding style={{ fontSize: "20px", marginLeft: "10px" }} />}
                    handleChange={dtLevelHandleChange}
                    reduxData={dtLevel}
                    data={DtLevelData?.data?.data}
                  />
                )}

                <FilterTab
                  child="AdType"
                  icon={
                    <ManageSearch style={{ fontSize: "20px", marginLeft: "10px" }} />
                  }
                  handleChange={adTypeHandleChange}
                  reduxData={adType}
                  data={AdTypeData?.data?.data}
                />

                <FilterTab
                  child="Category"
                  icon={<Category style={{ fontSize: "20px", marginLeft: "10px" }} />}
                  handleChange={categoryHandleChange}
                  reduxData={category}
                  data={CategoryData?.data?.data}
                />

                <FilterTab
                  child="Feature"
                  icon={
                    <Construction style={{ fontSize: "20px", marginLeft: "10px" }} />
                  }
                  handleChange={featureHandleChange}
                  reduxData={feature}
                  data={FeatureData?.data?.data}
                />



                <FilterTab
                  child="Brand"
                  icon={<Style style={{ fontSize: "20px", marginLeft: "10px" }} />}
                  handleChange={brandHandleChange}
                  reduxData={brand}
                  data={BrandData?.data?.data}
                />

                {SubBrandData?.data?.data.length !== 0 && brand.length !== 0 && (
                  <FilterTab
                    child="SubBrand"
                    icon={
                      <Loyalty style={{ fontSize: "20px", marginLeft: "10px" }} />
                    }
                    handleChange={subBrandHandleChange}
                    reduxData={subBrand}
                    data={SubBrandData?.data?.data}
                  />
                )}

                {BrandCollabData?.data?.data.length !== 0 && brand.length !== 0 && (
                  <FilterTab
                    child="Brand Collab"
                    icon={
                      <CollectionsBookmark
                        style={{ fontSize: "20px", marginLeft: "10px" }}
                      />
                    }
                    handleChange={brandCollabHandleChange}
                    reduxData={brandCollab}
                    data={BrandCollabData?.data?.data}
                  />
                )}

                <FilterTab
                  child="Language"
                  icon={
                    <Translate style={{ fontSize: "20px", marginLeft: "10px" }} />
                  }
                  handleChange={languageHandleChange}
                  reduxData={language}
                  data={LanguageData?.data?.data}
                />

                <FilterTab
                  child="Orientation"
                  icon={
                    <ScreenRotation
                      style={{ fontSize: "20px", marginLeft: "10px" }}
                    />
                  }
                  handleChange={orientationHandleChange}
                  reduxData={orientation}
                  data={OrientationData?.data?.data}
                />

                <FilterTab
                  child="Stage"
                  icon={<Approval style={{ fontSize: "20px", marginLeft: "10px" }} />}
                  handleChange={stageHandleChange}
                  reduxData={stage}
                  data={StageData?.data?.data}
                />

                <FilterTab
                  child="Year"
                  icon={
                    <CalendarToday style={{ fontSize: "20px", marginLeft: "10px" }} />
                  }
                  handleChange={yearHandleChange}
                  reduxData={year}
                  data={YearData?.data?.data}
                />
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-4 flex items-center justify-between">
                <button
                  onClick={() => logoutHandler()}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
                >
                  <span>→</span>
                  <span>Logout</span>
                </button>
                <button
                  onClick={() => setShowMenu(false)}
                  className="bg-red-600 text-white px-6 py-2 rounded font-semibold hover:bg-red-700"
                >
                  GO!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function SidebarAdmin() {
  let data: authDetails = getAuthFromLocal();
  const navigate = useNavigate();

  function logoutHandler() {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "Engage_Tap_Logout",
        category: "User Interaction",
        action: "Button Click",
        label: "Logout Button",
      });
    }
    removeAuthFromLocal();
    navigate("/");
  }
  // //console.log(data, "is data");

  !data && navigate("/");
  data && data?.roles === "user" && navigate("/");

  return (
    <div className="sticky top-[10vh] w-[20vw] max-[800px]:w-[30vw] h-[90vh] bg-gray-900 px-[2vw] py-[2vh] flex flex-col shadow-lg justify-between">
      <div className="h-[90%] relative">
        <div className="text-gray-500 flex justify-between w-full h-max pb-4 sticky top-0 bg-gray-900">
          <p className="flex items-center gap-1 font-semibold text-xl">
            Filters
          </p>
        </div>
        <div className="overflow-y-auto h-[90%] scrollerRed">
          <FilterTabV2 href="/admin"> creative </FilterTabV2>
          <FilterTabV2 href="/admin/adType"> adType </FilterTabV2>
          <FilterTabV2 href="/admin/brand"> brand </FilterTabV2>
          <FilterTabV2 href="/admin/subBrand"> subBrand </FilterTabV2>
          <FilterTabV2 href="/admin/brandCollab"> brandCollab </FilterTabV2>
          <FilterTabV2 href="/admin/dtLevels"> Dt Levels </FilterTabV2>
          <FilterTabV2 href="/admin/domain"> Domain </FilterTabV2>
          <FilterTabV2 href="/admin/feature"> feature </FilterTabV2>
          <FilterTabV2 href="/admin/keyword"> keyword </FilterTabV2>
          <FilterTabV2 href="/admin/specialKeyword">specialKeyword</FilterTabV2>
          <FilterTabV2 href="/admin/category"> category </FilterTabV2>
          <FilterTabV2 href="/admin/creativeFormat">creativeFormat</FilterTabV2>
          <FilterTabV2 href="/admin/region"> region </FilterTabV2>
          <FilterTabV2 href="/admin/country"> country </FilterTabV2>
          <FilterTabV2 href="/admin/language"> language </FilterTabV2>
          <FilterTabV2 href="/admin/orientation"> orientation </FilterTabV2>
          <FilterTabV2 href="/admin/stage"> stage </FilterTabV2>
          <FilterTabV2 href="/admin/year"> year </FilterTabV2>
          <FilterTabV2 href="/admin/bulkKeywordUpdate">
            Bulk Keyword Update
          </FilterTabV2>
          {data && data?.roles === "admin" && (
            <FilterTabV2 href="/admin/users"> Users </FilterTabV2>
          )}
        </div>
      </div>
      <button
        type={"button"}
        onClick={() => logoutHandler()}
        className={`w-1/2 min-w-max mx-auto my-1 flex gap-1 justify-center items-center capitalize p-2 px-4 border-none rounded-xl whitespace-nowrapl hover:bg-[#a90027] text-[#DA1A42] hover:text-white`}
      >
        log out
      </button>
    </div>
  );
}
